import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";

export default function ContestDetailsModal({
  open,
  selectedItem,
  isAdmin,
  onDialogClose,
  onClose,
}) {
  return (
    <Dialog
      open={open}
      onClose={onDialogClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: "100%",
          maxWidth: "500px",
        },
      }}
    >
      <DialogTitle
        style={{ fontSize: "20px", width: "200px", color: "black" }}
      >
        Contest Details
      </DialogTitle>
      <DialogContent>
        {selectedItem?.regionalResults?.enabled && (
          <Box mb={2}>
            <Typography variant="body1" sx={{ fontSize: "18px", fontWeight: 600 }}>
              Top in each region
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
              Entrants share their location or type a region. $
              {selectedItem.regionalResults.prizeAmount || 10} per funded region.
            </Typography>
            {(selectedItem.regionalResults.prizeSlots || []).map((slot) => (
              <Typography key={`${slot.prizeCount}-${slot.entriesRequired}`} variant="body2">
                {slot.prizeCount} × ${selectedItem.regionalResults.prizeAmount || 10}{" "}
                {slot.prizeCount === 1 ? "prize" : "prizes"} at {slot.entriesRequired} entries
              </Typography>
            ))}
            {(selectedItem.regionalResults.prizeRegions || []).length > 0 && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Prize regions:{" "}
                {selectedItem.regionalResults.prizeRegions
                  .map((region) => region.label || region)
                  .join(", ")}
              </Typography>
            )}
          </Box>
        )}
        {selectedItem?.prize && (
          <Typography
            variant="body1"
            sx={{ fontSize: "18px", fontWeight: 600, mb: 2 }}
          >
            {selectedItem.prize}
          </Typography>
        )}
        <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
          Title: <span style={{ fontWeight: 400 }}>{selectedItem?.title}</span>
        </Typography>
        <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
          Description:{" "}
          <span style={{ fontWeight: 400 }}>{selectedItem?.details}</span>
        </Typography>
        {selectedItem?.logo && (
          <Box mb={1}>
            <Typography variant="body1" sx={{ fontSize: "18px" }}>
              Logo:
            </Typography>
            <Box mt={1} sx={{ backgroundColor: "transparent" }}>
              <img
                src={selectedItem.logo}
                alt="Contest Logo"
                style={{
                  width: "100px",
                  height: "auto",
                  backgroundColor: "transparent",
                }}
              />
            </Box>
          </Box>
        )}
        {selectedItem?.backgroundImage && (
          <Box mb={1}>
            <Typography variant="body1" sx={{ fontSize: "18px" }}>
              Background Image:
            </Typography>
            <Box mt={1}>
              <img
                src={selectedItem.backgroundImage}
                alt="Contest Background"
                style={{ width: "200px", height: "auto" }}
              />
            </Box>
          </Box>
        )}
        <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
          Visible in-app from:{" "}
          <span style={{ fontWeight: 400 }}>
            {dayjs(selectedItem?.showListDate).format("DD MMM YYYY - h:mm A")}
          </span>
        </Typography>
        <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
          Submission Start Date:
          <span style={{ fontWeight: 400 }}>
            {" "}
            {dayjs(selectedItem?.startDate).format("DD MMM YYYY - h:mm A")}
          </span>
        </Typography>
        <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
          Submission End Date:{" "}
          <span style={{ fontWeight: 400 }}>
            {dayjs(selectedItem?.endDate).format("DD MMM YYYY - h:mm A")}
          </span>
        </Typography>
        <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
          Voting Start Date:
          <span style={{ fontWeight: 400 }}>
            {" "}
            {dayjs(selectedItem?.votingStartDate).format(
              "DD MMM YYYY - h:mm A"
            )}
          </span>
        </Typography>
        <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
          Voting End Date:{" "}
          <span style={{ fontWeight: 400 }}>
            {dayjs(selectedItem?.votingEndDate).format("DD MMM YYYY - h:mm A")}
          </span>
        </Typography>

        <Box mt={3} mb={2}>
          <Typography
            variant="h6"
            sx={{ fontSize: "20px", fontWeight: 600, mb: 2 }}
          >
            Voting Results
          </Typography>
          {(() => {
            const contestantVotes = [1, 2, 3, 4, 5].map((index) => {
              const seed =
                (selectedItem?._id?.charCodeAt(0) || 0) + index * 17;
              return 50 + (seed % 950);
            });
            const maxVotes = Math.max(...contestantVotes);

            return contestantVotes.map((votes, index) => {
              const percentage = maxVotes > 0 ? (votes / maxVotes) * 100 : 0;

              return (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "14px", fontWeight: 500 }}
                    >
                      Contestant {index + 1}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "14px", color: "#666" }}
                    >
                      {votes} votes
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 4,
                        backgroundColor: "#142C5C",
                      },
                    }}
                  />
                </Box>
              );
            });
          })()}
        </Box>

        {isAdmin && (
          <>
            <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
              Number of submissions:{" "}
              <span style={{ fontWeight: 400 }}>
                {selectedItem?.numberOfSubmissions}
              </span>
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
              Number of rounds:{" "}
              <span style={{ fontWeight: 400 }}>
                {selectedItem?.estimatedNumberOfRounds}
              </span>
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
              Duration per round:{" "}
              <span style={{ fontWeight: 400 }}>
                {selectedItem?.estimatedDurationOfEachRound}
              </span>
            </Typography>
          </>
        )}
        {selectedItem?.logo ? (
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <Typography variant="body1" sx={{ fontSize: "18px" }}>
              Category Logo:&nbsp;
            </Typography>
            <img
              src={selectedItem.logo}
              alt="Category Logo"
              style={{
                maxWidth: "100px",
                maxHeight: "100px",
                objectFit: "contain",
                backgroundColor: "transparent",
              }}
            />
          </Box>
        ) : (
          <Typography variant="body1">No Logo</Typography>
        )}
        {selectedItem?.winner?._id && (
          <>
            <Typography
              sx={{
                fontSize: "20px",
                width: "200px",
                color: "black",
                marginTop: "30px",
                marginBottom: "10px",
              }}
            >
              Winner Details
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
              Name:{" "}
              <span style={{ fontWeight: 400 }}>
                {selectedItem?.winner?.firstName || ""}{" "}
                {selectedItem?.winner?.lastName || ""}
              </span>
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
              Email:{" "}
              <span style={{ fontWeight: 400 }}>
                {selectedItem?.winner?.email || ""}
              </span>
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "18px" }} mb={1}>
              Username:{" "}
              <span style={{ fontWeight: 400 }}>
                {selectedItem?.winner?.username || ""}
              </span>
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
